import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { CodeInput } from '@/components/code-input';
import { Spinner } from '@/components/spinner';
import type { Copy } from '@/lib/messages';

interface CodeStepProps {
  copy: Copy;
  pending: boolean;
  error: string | null;
  notice: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onRestart: () => void;
}

const CODE_LENGTH = 6;

/**
 * Seconds before "send a new code" comes back. The server allows 3 sends per
 * 10 minutes per address, so this is not the control that enforces the limit —
 * it just stops an impatient double-tap from spending the whole budget in two
 * seconds and turning the next real attempt into a rate-limit error.
 */
const RESEND_COOLDOWN_S = 30;

export function CodeStep({
  copy,
  pending,
  error,
  notice,
  onSubmit,
  onResend,
  onRestart,
}: CodeStepProps) {
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const wasPending = useRef(false);
  const input = useRef<HTMLInputElement>(null);

  // A code was just sent to land on this step, so the clock starts immediately
  // rather than on the first resend.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // A rejected code is wrong as a whole, so the field empties for a clean
  // retry. It also closes the trap the auto-submit below would otherwise open:
  // correcting one digit of a rejected code would re-complete it and spend
  // another of the five attempts on a code already known to be wrong.
  useEffect(() => {
    // Keyed on the request FINISHING with an error still present — not on the
    // error string changing, because the error now survives the retry submit
    // (see the reducer) and two identical failures in a row must both clear.
    if (wasPending.current && !pending && error) {
      setCode('');
      // The field kept focus through the request (readOnly, not disabled),
      // but a cleared value plus an explicit focus keeps this robust if that
      // ever changes.
      input.current?.focus();
    }
    wasPending.current = pending;
  }, [pending, error]);

  function handleChange(next: string) {
    setCode(next);
    // Six digits is the whole answer — there is nothing left to review, so
    // asking for a click as well is pure friction. This also means an OS
    // autofill of the emailed code signs the user in without them touching
    // anything (docs/auth/35-design/components.md).
    if (next.length === CODE_LENGTH && !pending) onSubmit(next);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (code.length === CODE_LENGTH) onSubmit(code);
  }

  const previousNotice = useRef(notice);

  // The cooldown restarts when the resend actually SUCCEEDED (the notice
  // appearing is that signal) — restarting it optimistically meant a 429'd
  // resend still locked the button for thirty seconds with no code coming.
  useEffect(() => {
    if (notice && !previousNotice.current) setCooldown(RESEND_COOLDOWN_S);
    previousNotice.current = notice;
  }, [notice]);

  function handleResend() {
    // The old code is dead the moment a new one is issued; keeping the digits
    // around only invites submitting them.
    setCode('');
    onResend();
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {error ? <Alert tone="error">{error}</Alert> : null}
      {notice ? <Alert tone="info">{notice}</Alert> : null}
      <CodeInput
        label={copy.codeLabel}
        value={code}
        onChange={handleChange}
        readOnly={pending}
        inputRef={input}
        length={CODE_LENGTH}
        hint={copy.codeHint(CODE_LENGTH)}
      />
      {/* No submit button. A full code submits itself, so the button could
          only ever sit there disabled — contradicting the hint above it and
          implying the user has forgotten to do something. The hidden one keeps
          Enter working for anyone who reaches for it out of habit. */}
      <button
        type="submit"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      >
        {copy.codeSubmit}
      </button>

      {/* Not height-reserved: an always-present empty row reads as a missing
          element, and the only thing this can shift is a set of links that are
          disabled for exactly as long as it is showing. The Spinner carries
          its own live region, so this wrapper must not add a second one. */}
      {pending ? (
        <div className="flex items-center justify-center gap-2 text-sm text-buds-muted">
          <Spinner label={copy.finishing} />
          {copy.finishing}
        </div>
      ) : null}

      {/* gap-3: at gap-2 the two 28px links sat 36px centre-to-centre, and a
          thumb aiming for "Send a new code" hit "Use a different email" —
          which restarts the flow and orphans the code already in the inbox. */}
      <div className="flex flex-col items-center gap-3 text-sm">
        <Button
          type="button"
          variant="link"
          onClick={handleResend}
          disabled={pending || cooldown > 0}
        >
          {cooldown > 0 ? copy.codeResendIn(cooldown) : copy.codeResend}
        </Button>
        <Button
          type="button"
          variant="link"
          onClick={onRestart}
          disabled={pending}
        >
          {copy.back}
        </Button>
      </div>
    </form>
  );
}
